/**
 * @file AudioCueController.ts
 * @role Maps the typed presentation notifications that the choreography matrix CANNOT express to
 *       optional audio cues: the per-reel, per-symbol, and per-payline ticks whose count depends
 *       on the outcome rather than on an authored timeline.
 * @owns Those per-item cues and the reel loop's stop on reel settle.
 * @reads EventBus notifications that have already occurred.
 * @neverDoes Control gameplay, infer outcomes, plan reel stops, or delay a state transition.
 *            Play a win sting, the count loop, the count end, spin start, or the error cue —
 *            PresentationSequenceCoordinator owns those, timed by the matrix (ADR 0010).
 * @invariants 1, 5, 9, 13.
 * @see docs/decisions/0010-explicit-presentation-choreography.md
 */
import type { EventBus } from '@core/events/EventBus';
import type { AudioService } from './AudioService';

export class AudioCueController {
  private readonly unsubscribe: Array<() => void> = [];

  constructor(
    events: EventBus,
    private readonly audio: AudioService,
  ) {
    this.unsubscribe.push(
      events.on('ui:activated', () => audio.play('ui_click')),
      // One cue per reel, pitched by index. The matrix has no step per reel because the reel
      // count is a config/outcome property, not an authored beat.
      events.on('reel:settled', ({ reelIndex, isFinal }) => {
        if (isFinal) {
          audio.play('reel_stop_final');
          return;
        }
        const rate = 0.96 + Math.min(Math.max(reelIndex, 0), 4) * 0.02;
        audio.play('reel_stop', { rate });
      }),
      events.on('spin:reelsStopped', () => audio.stop('reel_spin_loop')),
      // One cue per activated symbol and per completed payline: how many there are depends on
      // the win the server sent, so these follow the explanation rather than the score.
      events.on('win:symbolActivated', () => audio.play('win_symbol_hit')),
      events.on('win:paylineCompleted', () => audio.play('win_payline_complete')),
    );
  }

  dispose(): void {
    for (const unsubscribe of this.unsubscribe.splice(0)) unsubscribe();
    this.audio.stop('reel_spin_loop', 0);
    this.audio.stop('win_count_loop', 0);
  }
}
